import { useHotkeys } from 'react-hotkeys-hook';
import { getStore, usePlayerStore } from '../../../stores/StoreContext';
import CONSTANTS from '../../../config/constants';

/**
 * Custom hook that manages all player keyboard shortcuts and hotkeys.
 * Centralizes hotkey logic for the video player to avoid duplication and
 * provide consistent keyboard controls across the application.
 * 
 * Supported Hotkeys:
 * - Space: Toggle play/pause
 * - Left Arrow: Seek backward 5 seconds
 * - Right Arrow: Seek forward 5 seconds
 * - Up Arrow: Increase playback speed
 * - Down Arrow: Decrease playback speed
 * - F: Toggle fullscreen
 * - N: Add a note (placeholder)
 * - R: Reset video state (useful when video gets stuck)
 * - 0: Set playback speed to 1x (normal)
 * - 1: Set playback speed to 0.5x
 * - 3: Set playback speed to 2x
 * - 4: Set playback speed to 5x
 * - 5: Set playback speed to 10x
 * 
 * @returns {void} This hook doesn't return anything but sets up hotkey listeners
 * 
 * @example
 * // Use in any component that needs player hotkeys
 * import { usePlayerHotkeys } from '../hooks/usePlayerHotkeys';
 * 
 * const MyComponent = () => {
 *   usePlayerHotkeys();
 *   return <div>Component content</div>;
 * };
 */
export const usePlayerHotkeys = () => {
  const PlayerStore = getStore('playerStore');
  const isPlaying = usePlayerStore((state) => state.playing);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
  const fullscreen = usePlayerStore((state) => state.fullscreen);
  const muted = usePlayerStore((state) => state.muted);

  // Space: Toggle play/pause
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.PAUSE?.KEY) {
      useHotkeys(
        CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.PAUSE.KEY,
        (event) => {
          event.preventDefault();
          PlayerStore.setPlaying(!isPlaying);
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      },
      [isPlaying]
    );
  }

  // Left Arrow: Seek backward 10 seconds
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.SEEK_BACKWARD_10_SECONDS?.KEY) {
      useHotkeys(
        CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.SEEK_BACKWARD_10_SECONDS.KEY,
        (event) => {
          event.preventDefault();
          const newTime = Math.max(0, currentTime - 5);
          PlayerStore.seekVideoTo(newTime);
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      },
      [currentTime]
    );
  }

  // Right Arrow: Seek forward 10 seconds
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.SEEK_FORWARD_10_SECONDS?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.SEEK_FORWARD_10_SECONDS.KEY,
      (event) => {
        event.preventDefault();
        const newTime = currentTime + 5;
        PlayerStore.seekVideoTo(newTime);
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      },
      [currentTime]
    );
  }

  // F: Toggle fullscreen
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.TOGGLE_FULLSCREEN?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.TOGGLE_FULLSCREEN.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setFullScreen(!fullscreen);
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      },
      [fullscreen]
    );
  }

  // N: Add a note
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.NOTE?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.NOTE.KEY,
      (event) => {
        event.preventDefault();
        console.log('Add a note...');
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      } 
    );
  }

  // R: Reset video state (useful when video gets stuck)
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.RESET_VIDEO_STATE?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.RESET_VIDEO_STATE.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.resetVideoState();
      },
      { 
        enableOnContentEditable: false,
        preventDefault: true
      }
    );
  }

  // Number keys: Set playback speed directly
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.SLOW?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.SLOW.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(0.5);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.NORMAL?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.NORMAL.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(1.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.FAST_2X?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.FAST_2X.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(2.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.FAST_5X?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.FAST_5X.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(5.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.FAST_10X?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.FAST_10X.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(10.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.FAST_16X?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.FAST_16X.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(16.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.SMART?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.PLAYBACK.OPTIONS.SMART.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setPlaybackSpeed(100.0);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  // Toggle video labels visibility
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.VIDEO_LABELS?.TOGGLE?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.TOGGLE.KEY,
      (event) => {
        event.preventDefault();
        const currentValue = PlayerStore.displayVideoLabels;
        const newValue = currentValue === 'show' ? 'hide' : 'show';
        PlayerStore.setDisplayVideoLabels(newValue);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.VIDEO_LABELS?.SHOW?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.SHOW.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setDisplayVideoLabels('show');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.VIDEO_LABELS?.HIDE?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.HIDE.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setDisplayVideoLabels('hide');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  // Toggle annotation markers
  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.FLAGS?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.FLAGS.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.toggleMarker('flags');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.EVENTS?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.EVENTS.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.toggleMarker('events');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.PHASES?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.PHASES.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.toggleMarker('phases');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.CONNECTION?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.CONNECTION.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.toggleMarker('connection');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.NOTES?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.NOTES.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.toggleMarker('notes');
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

  if (CONSTANTS?.KEYBOARD_SHORTCUTS?.VOLUME?.OPTIONS?.MUTE?.KEY) {
      useHotkeys(
      CONSTANTS.KEYBOARD_SHORTCUTS.VOLUME.OPTIONS.MUTE.KEY,
      (event) => {
        event.preventDefault();
        PlayerStore.setMuted(!muted);
      },
      { enableOnContentEditable: false, preventDefault: true }
    );
  }

};

export default usePlayerHotkeys;
